import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { categories } from "../../data";
import { catalogue } from "../../catalogue";

// Build product options grouped by category: 134 individual products
// Format: "Education · VLSI Trainers · Universal VLSI Trainer - 01"
function buildOptionGroups() {
  const groups = categories.map((cat) => {
    const subs = catalogue.filter((sub) => sub.category === cat.slug);
    const items = [];
    subs.forEach((sub) => {
      sub.products.forEach((p) => {
        items.push({
          value: `${cat.title} · ${p.name}`,
          label: `${sub.title} → ${p.name}`,
        });
      });
    });
    return { groupLabel: cat.title, items };
  });
  return groups;
}

const OPTION_GROUPS = buildOptionGroups();

function Field({ label, children, required, span }) {
  return (
    <label className={`flex flex-col gap-2 ${span ? "md:col-span-2" : ""}`}>
      <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-textmuted">
        {label} {required && <span className="text-signal-amber">*</span>}
      </span>
      {children}
    </label>
  );
}

export function EnquirySuccess({ onReset }) {
  return (
    <div
      className="border border-signal-phosphor/40 bg-signal-phosphor/[0.04] p-10"
      data-testid="enquiry-success"
    >
      <CheckCircle2 size={28} className="text-signal-phosphor mb-4" />
      <p className="font-display font-bold text-2xl mb-3">Enquiry sent.</p>
      <p className="font-mono text-sm text-textmuted leading-relaxed mb-6">
        Thank you — your message has been delivered to{" "}
        <span className="text-signal-amber">sales@ceaindia.in</span>. A member of
        our team will respond within one business day.
      </p>
      <button className="btn-ghost" onClick={onReset} data-testid="enquiry-send-another">
        Send another <ArrowUpRight size={14} />
      </button>
    </div>
  );
}

export function EnquiryForm({ form, status, onChange, onSubmit }) {
  // Check if prefilled value is already in the option list
  const allValues = new Set(OPTION_GROUPS.flatMap((g) => g.items.map((i) => i.value)));
  const prefillIsCustom = form.product_interest && !allValues.has(form.product_interest);

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="enquiry-form">
      <Field label="Full name" required>
        <input name="name" required className="terminal" value={form.name} onChange={onChange}
          placeholder="Your name" maxLength={120} data-testid="enquiry-input-name" />
      </Field>
      <Field label="Email" required>
        <input type="email" name="email" required className="terminal" value={form.email} onChange={onChange}
          placeholder="you@company.com" data-testid="enquiry-input-email" />
      </Field>
      <Field label="Phone">
        <input name="phone" className="terminal" value={form.phone} onChange={onChange}
          placeholder="+91 ··· ··· ····" maxLength={40} data-testid="enquiry-input-phone" />
      </Field>
      <Field label="Company / Institution">
        <input name="company" className="terminal" value={form.company} onChange={onChange}
          placeholder="Organisation name" maxLength={160} data-testid="enquiry-input-company" />
      </Field>
      <Field label="Product interest" span>
        <select
          name="product_interest"
          className="terminal"
          value={form.product_interest}
          onChange={onChange}
          data-testid="enquiry-input-product"
        >
          <option value="">Select a category or product…</option>
          {prefillIsCustom && (
            <option value={form.product_interest}>{form.product_interest}</option>
          )}
          {OPTION_GROUPS.map((group) => (
            <optgroup key={group.groupLabel} label={group.groupLabel}>
              {group.items.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ))}
          <option value="Other / Custom requirement">Other / Custom requirement</option>
        </select>
      </Field>
      <Field label="Your message" span required>
        <textarea name="message" required rows={6} className="terminal" value={form.message}
          onChange={onChange} maxLength={4000}
          placeholder="Quantities, application, deadline, delivery location…"
          data-testid="enquiry-input-message" />
      </Field>

      {/* Honeypot — hidden from real users, picked up by spam bots */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={form.website || ""}
        onChange={onChange}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        aria-hidden="true"
      />

      {status.state === "error" && (
        <p className="md:col-span-2 font-mono text-xs text-red-400 border border-red-500/40 bg-red-500/[0.06] p-4"
          data-testid="enquiry-error">! {status.error}</p>
      )}

      <div className="md:col-span-2 flex items-center gap-4 mt-2">
        <button type="submit" className="btn-primary disabled:opacity-50"
          disabled={status.state === "loading"} data-testid="enquiry-submit">
          {status.state === "loading" ? "Sending…" : "Send enquiry"} <ArrowUpRight size={14} />
        </button>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-textmuted">
          Response &lt; 24h business
        </p>
      </div>
    </form>
  );
}
